'use client';

import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from '@phosphor-icons/react';

type CopyState = 'idle' | 'copied' | 'error';

interface CopyMarkdownButtonProps {
  markdown: string;
}

function copyWithFallback(value: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  return copied;
}

export default function CopyMarkdownButton({ markdown }: CopyMarkdownButtonProps) {
  const [state, setState] = useState<CopyState>('idle');
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
  }, []);

  const copyMarkdown = async () => {
    if (resetTimer.current) clearTimeout(resetTimer.current);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(markdown);
      } else if (!copyWithFallback(markdown)) {
        throw new Error('Clipboard is unavailable');
      }
      setState('copied');
    } catch {
      try {
        if (!copyWithFallback(markdown)) throw new Error('Copy failed');
        setState('copied');
      } catch {
        setState('error');
      }
    }

    resetTimer.current = setTimeout(() => setState('idle'), 2200);
  };

  const label = state === 'copied'
    ? 'Markdown copied'
    : state === 'error'
      ? 'Could not copy Markdown'
      : 'Copy post as Markdown';

  return (
    <div className="relative ml-auto shrink-0">
      <button
        type="button"
        onClick={copyMarkdown}
        aria-label={label}
        title={label}
        className={`peer inline-flex size-10 items-center justify-center rounded-[8px] border bg-[var(--portfolio-surface)] transition duration-200 ${
          state === 'copied'
            ? 'border-[#00B87A] text-[#008A5C]'
            : state === 'error'
              ? 'border-red-300 text-red-600'
              : 'border-[var(--portfolio-line)] text-[var(--portfolio-muted)] hover:-translate-y-px hover:border-[var(--portfolio-accent-text)] hover:text-[var(--portfolio-accent-text)]'
        }`}
      >
        {state === 'copied' ? (
          <Check size={20} aria-hidden="true" />
        ) : (
          <Copy size={20} aria-hidden="true" />
        )}
      </button>
      <span aria-hidden="true" className="pointer-events-none absolute right-0 top-full z-10 mt-2 w-max max-w-48 rounded-md bg-gray-900 px-2.5 py-1.5 text-xs text-white opacity-0 shadow-md transition-opacity peer-hover:opacity-100 peer-focus-visible:opacity-100">
        {label}
      </span>
      <span className="sr-only" aria-live="polite">{state === 'idle' ? '' : label}</span>
    </div>
  );
}
